import React, { useMemo, useState } from "react";

import { Badge, riskTone } from "./_shared/primitives";
import { humanBytes, relativeTime } from "./_shared/utils";

import "./FileSearchModal.scss";

// =============================================================================
// UserTeamPickerModal — sibling of FileSearchModal for the Users dashboard.
// Reuses the same `ysd-search-*` overlay + sidebar + results SCSS so the two
// pickers feel identical. Mode segmented control switches the result rows
// between user shape and team shape; "All" interleaves both.
//
// Props:
//   isOpen          — render gate
//   onClose         — invoked on outside click (when dismissible)
//   onSelectSubject — ({ kind: "user"|"team", key }) => void
//   variant         — "viewport" (full-screen) | "panel" (fills positioned ancestor)
//   dismissible     — whether outside-click closes; the empty-state hero passes
//                     `false` so the picker is always present until selection
//   users           — UserRow[]
//   teams           — TeamRow[]
//   recentSubjects  — { kind, key, at }[]
// =============================================================================

const RISK_TONE = (risk) => (risk === "high" ? "danger" : risk === "medium" ? "warning" : "neutral");
const STATUS_TONE = { active: "success", dormant: "warning", blocked: "danger", suspended: "neutral" };

const UserTeamPickerModal = ({
  isOpen,
  onClose,
  onSelectSubject,
  variant = "viewport",
  dismissible = true,
  users = [],
  teams = [],
  recentSubjects = [],
}) => {
  const [searchName, setSearchName] = useState("");
  const [modeFilter, setModeFilter] = useState("all"); // "users" | "teams" | "all"
  const [statusFilters, setStatusFilters] = useState([]); // active|dormant|blocked|suspended
  const [riskFilters, setRiskFilters]     = useState([]); // high|medium|low
  const [teamFilter, setTeamFilter]       = useState(""); // empty = any team

  const toggle = (setter) => (val) =>
    setter((prev) => (prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]));

  const clearAll = () => {
    setSearchName("");
    setStatusFilters([]);
    setRiskFilters([]);
    setTeamFilter("");
  };

  // Build rendered rows. `kind` lets us share one row component for both shapes.
  const userRows = useMemo(() => users.map((u) => ({
    kind: "user",
    key: u.email,
    title: `${u.firstname} ${u.lastname}`.trim() || u.email,
    initials: `${(u.firstname || u.email)[0] || ""}${(u.lastname || "")[0] || ""}`.toUpperCase() || "?",
    subtitle: u.email,
    role: u.role,
    teams: u.teams || [],
    metaLeft: u.location,
    metaRight: u.last_active ? `last active ${relativeTime(u.last_active)}` : null,
    status: u.status || (u.active ? "active" : "blocked"),
    risk: u.risk,
    storage_mb: u.storage_mb,
    raw: u,
  })), [users]);

  const teamRows = useMemo(() => teams.map((t) => ({
    kind: "team",
    key: t.name,
    title: `#${t.name}`,
    initials: t.name.slice(0, 2).toUpperCase(),
    subtitle: `${t.member_count ?? (t.members?.length ?? 0)} members`,
    role: (t.roles || []).join(" · "),
    teams: t.roles || [],
    metaLeft: t.owner_email ? `owner ${t.owner_email}` : null,
    metaRight: null,
    status: "active",
    risk: "n/a",
    raw: t,
  })), [teams]);

  // Filter + search.
  const matchingRows = useMemo(() => {
    const q = searchName.trim().toLowerCase();
    const teamFilterSet = teamFilter ? new Set([teamFilter]) : null;

    let pool = [];
    if (modeFilter === "users")      pool = userRows;
    else if (modeFilter === "teams") pool = teamRows;
    else                              pool = [...userRows, ...teamRows];

    return pool.filter((r) => {
      if (q) {
        const hay = `${r.title} ${r.subtitle} ${r.role} ${(r.teams || []).join(" ")} ${r.metaLeft || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilters.length && !statusFilters.includes(r.status)) return false;
      if (riskFilters.length && !riskFilters.includes(r.risk)) return false;
      if (teamFilterSet) {
        const inTeam = r.kind === "user"
          ? (r.raw.teams || []).some((t) => teamFilterSet.has(t))
          : teamFilterSet.has(r.key);
        if (!inTeam) return false;
      }
      return true;
    });
  }, [modeFilter, userRows, teamRows, searchName, statusFilters, riskFilters, teamFilter]);

  // ── Suggested rails ──────────────────────────────────────────────────────
  // Recently viewed: resolve from props.recentSubjects against current pools.
  const recentRail = useMemo(() => {
    const usersByKey = new Map(userRows.map((r) => [r.key, r]));
    const teamsByKey = new Map(teamRows.map((r) => [r.key, r]));
    return recentSubjects
      .map((ref) => (ref.kind === "user" ? usersByKey.get(ref.key) : teamsByKey.get(ref.key)))
      .filter(Boolean)
      .slice(0, 5);
  }, [recentSubjects, userRows, teamRows]);

  // Anomalous: top 5 users by risk band (high → medium). PR3 will replace this
  // with `riskScores` numeric ranking; for now we use the existing `risk` chip
  // so the rail isn't empty.
  const anomalousRail = useMemo(() => {
    const ORDER = { high: 3, medium: 2, low: 1, "n/a": 0 };
    return [...userRows]
      .filter((r) => r.risk === "high" || r.risk === "medium")
      .sort((a, b) => (ORDER[b.risk] || 0) - (ORDER[a.risk] || 0))
      .slice(0, 5);
  }, [userRows]);

  // Dormant → active: users whose last_active is older than 14d but who still
  // have events in their bundle. Approximated here via raw.last_active alone,
  // since the bundle isn't passed into the picker yet.
  const dormantRail = useMemo(() => {
    const cutoff = Date.now() - 14 * 86_400_000;
    return userRows
      .filter((r) => r.raw.last_active && Date.parse(r.raw.last_active) < cutoff && r.status === "active")
      .slice(0, 4);
  }, [userRows]);

  if (!isOpen) return null;

  const select = (row) => {
    onSelectSubject?.({ kind: row.kind, key: row.key });
    onClose?.();
  };

  const hasFilter = searchName || statusFilters.length || riskFilters.length || teamFilter;

  return (
    <div
      className={`ysd-search-overlay ysd-search-overlay--${variant}`}
      onClick={dismissible ? onClose : undefined}
    >
      <div className="ysd-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ysd-search-header">
          <div className="ysd-search-header-left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <span className="ysd-search-title">Find a user {/* or team */}</span>
            <span className="ysd-search-hint">⌘K · search · pick a subject</span>

          </div>
          {dismissible && (
            <button className="ysd-search-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className="ysd-search-body">
          <div className="ysd-search-sidebar">
            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Search</label>
              <input
                className="ysd-filter-input"
                placeholder="Name, email, role, location…"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Mode</label>
              <div className="ysd-filter-chips">
                {[
                  { v: "all",   l: "All" },
                  { v: "users", l: "Users" },
                  /* { v: "teams", l: "Teams" }, */
                ].map((o) => (

                  <button
                    key={o.v}
                    type="button"
                    className={`ysd-filter-chip ${modeFilter === o.v ? "active" : ""}`}
                    onClick={() => setModeFilter(o.v)}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Status</label>
              <div className="ysd-filter-chips">
                {["active", "dormant", "blocked", "suspended"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`ysd-filter-chip ${statusFilters.includes(s) ? "active" : ""}`}
                    onClick={() => toggle(setStatusFilters)(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="ysd-filter-group">
              <label className="ysd-filter-label">Risk</label>
              <div className="ysd-filter-chips">
                {["high", "medium", "low"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`ysd-filter-chip ${riskFilters.includes(r) ? "active" : ""}`}
                    onClick={() => toggle(setRiskFilters)(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* <div className="ysd-filter-group">
              <label className="ysd-filter-label">Team</label>
              <select
                className="ysd-filter-input"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="">Any team</option>
                {teams.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name} ({t.member_count ?? t.members?.length ?? 0})
                  </option>
                ))}
              </select>
            </div> */}


            <div className="ysd-filter-actions">
              <button
                type="button"
                className="ysd-filter-btn-clear"
                onClick={clearAll}
                disabled={!hasFilter}
              >
                Clear
              </button>
              <span className="ysd-filter-count">
                {matchingRows.length} match{matchingRows.length === 1 ? "" : "es"}
              </span>
            </div>
          </div>

          <div className="ysd-search-results">
            {/* Suggested rails — only show when the user hasn't started filtering. */}
            {!hasFilter && !!(recentRail.length || anomalousRail.length || dormantRail.length) && (
              <div className="ysd-suggested-rails">
                <SuggestedRail
                  title="Anomalous"
                  hint="Highest risk first"
                  rows={anomalousRail}
                  onPick={select}
                />
                <SuggestedRail
                  title="Dormant → active"
                  hint="Was idle, returned recently"
                  rows={dormantRail}
                  onPick={select}
                />
                <SuggestedRail
                  title="Recently viewed"
                  hint="Last opened on this browser"
                  rows={recentRail}
                  onPick={select}
                />
              </div>
            )}

            {matchingRows.length === 0 ? (
              <div className="ysd-results-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /><path d="M8 11h6" />
                </svg>
                <p>No users {/* or teams */} match these filters.</p>

              </div>
            ) : (
              <div className="ysd-results-list">
                {matchingRows.map((r) => (
                  <SubjectRow key={`${r.kind}:${r.key}`} row={r} onPick={select} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact suggested-rail: small horizontal scroller of subject chips. Keeps
// the picker dense — three rails fit in the same vertical space as five list
// rows but expose 12+ candidates an analyst is most likely to want.
const SuggestedRail = ({ title, hint, rows, onPick }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="ysd-suggested-rail">
      <div className="ysd-suggested-rail__head">
        <strong>{title}</strong>
        <small>{hint}</small>
      </div>
      <div className="ysd-suggested-rail__items">
        {rows.map((r) => (
          <button
            key={`${r.kind}:${r.key}`}
            type="button"
            className="ysd-suggested-chip"
            onClick={() => onPick(r)}
          >
            <span className="ysd-suggested-chip__avatar">{r.initials}</span>
            <span className="ysd-suggested-chip__text">
              <strong>{r.title}</strong>
              <small>{r.subtitle}</small>
            </span>
            {r.kind === "user" && r.risk && r.risk !== "n/a" && (
              <Badge tone={RISK_TONE(r.risk)} dot>{r.risk}</Badge>
            )}
            {r.kind === "team" && <Badge tone="brand">team</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
};

// Subject result row — used for both users and teams. Kind-specific badges
// keep the row scannable without the analyst hunting for a "type" column.
const SubjectRow = ({ row, onPick }) => (
  <div className="ysd-result-row" onClick={() => onPick(row)} role="button" tabIndex={0}>
    <span className="ysd-result-icon ysd-subject-avatar">{row.initials}</span>
    <div className="ysd-result-detail">
      <span className="ysd-result-name">
        {row.title}
        {row.kind === "team" && <Badge tone="brand">team</Badge>}
        {row.kind === "user" && row.risk && row.risk !== "n/a" && (
          <Badge tone={riskTone(row.risk)} dot>{row.risk} risk</Badge>
        )}
        {row.kind === "user" && row.status && (
          <Badge tone={STATUS_TONE[row.status] || "neutral"} dot>{row.status}</Badge>
        )}
      </span>
      <div className="ysd-result-meta">
        {row.subtitle && <span>{row.subtitle}</span>}
        {row.role && <span>· {row.role}</span>}
        {row.metaLeft && <span>· {row.metaLeft}</span>}
        {row.metaRight && <span>· {row.metaRight}</span>}
        {row.kind === "user" && typeof row.storage_mb === "number" && (
          <span>· {humanBytes(row.storage_mb * 1024 * 1024)}</span>
        )}
        {(row.teams || []).slice(0, 3).map((t) => (
          <Badge key={t} tone="info">{row.kind === "team" ? t : `#${t}`}</Badge>
        ))}
        {(row.teams || []).length > 3 && (
          <Badge tone="neutral">+{(row.teams || []).length - 3}</Badge>
        )}
      </div>
    </div>
  </div>
);

export default UserTeamPickerModal;
